import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type { ProductInformationFormType } from "@/types";
import type { YesNoOption } from "@/utils";

interface Address {
  addressLineOne: string;
  addressLineTwo?: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface MultiStepFormData {
  generalInformationFormData?: {
    listId: string;
    leadSourceName: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    dateOfBirth: { month: number; day: number; year: number };
    vendorId?: string;
  };
  medicalInformationFormData?: {
    isTakingInsulin: YesNoOption;
    insulinNames?: string[];
    dailyInsulinDose?: string;
    hasLowBloodSugar?: YesNoOption;
  };
  insuranceInformationFormData?: {
    primaryPayer: string;
    primaryPayerMbi: string;
    secondaryPayer: string;
    secondaryPayerId: string;
    hmo: YesNoOption;
    ppo: YesNoOption;
    hasSupplementaryPolicy: YesNoOption;
    supplementaryPolicyName?: string;
    supplementaryPolicyNumber?: string;
    coInsurancePercentage?: string;
    planEffectiveDate: string;
    msp: {
      MSPType: string;
      policyNumber: string;
      insurerName: string;
      address1: string;
      city: string;
      state: string;
      zip: string;
      startDate: string;
      endDate: string;
    } | null;
  };
  addressInformationFormData?: {
    cellPhoneNumber: string;
    billingAddress: Address;
    shippingAddress?: Address;
    hasSameShippingAddress: YesNoOption;
  };
  productInformationFormData?: ProductInformationFormType;
  doctorInformationFormData?: {
    doctor: {
      firstName: string;
      lastName: string;
      specialty: string;
      npi: string;
      phoneNumber: string;
      faxNumber: string;
      addressLineOne: string;
      addressLineTwo?: string;
      practiceAddress: string;
      city: string;
      state: string;
      zipCode: string;
    };
    hasGoogleMatch: boolean;
  };
  reviewInformationFormData?: {
    hasTextCommunication: YesNoOption;
    notes?: string;
  };
  transferInformationFormData?: {
    payorId: string;
  };
}

export interface MultiStepFormState {
  patientIdToTransfer: string;
  currentFormStep: number;
  multiStepFormData: MultiStepFormData | undefined;
  goToNextFormStep(): void;
  goToPreviousFormStep(): void;
  goToSpecificFormStep(step: number): void;
  setMultiStepFormData(multiStepFormData: MultiStepFormData): void;
  setPatientIdToTransfer(patientId: string): void;
  clearMultiStepFormData: () => void;
}

export const useMultiStepFormStore = create<MultiStepFormState>()(
  persist(
    (set) => ({
      patientIdToTransfer: "",
      currentFormStep: 1,
      multiStepFormData: undefined,
      goToNextFormStep: () => {
        set(({ currentFormStep }) => ({
          currentFormStep: currentFormStep + 1,
        }));
      },
      goToPreviousFormStep: () => {
        set(({ currentFormStep }) => ({
          currentFormStep: currentFormStep - 1,
        }));
      },
      goToSpecificFormStep: (step) => {
        set(() => ({
          currentFormStep: step,
        }));
      },
      setMultiStepFormData: (update: Partial<MultiStepFormData>) => {
        set((prevState) => ({
          multiStepFormData: {
            ...prevState.multiStepFormData,
            ...update,
          },
        }));
      },
      clearMultiStepFormData: () => {
        set({
          patientIdToTransfer: "",
          currentFormStep: 1,
          multiStepFormData: undefined,
        });
      },
      setPatientIdToTransfer: (patientId: string) => {
        set(() => ({
          patientIdToTransfer: patientId,
        }));
      },
    }),
    {
      name: "multiStepFormStore",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
